import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, LogOut, RefreshCw, Target, AlertTriangle, Calendar,
  FileText, Zap, Flame, ChevronRight, Clock, DollarSign,
  User, Phone, Mail, CheckCircle, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TodayTasks() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchTodayTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/login');
        return;
      }
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Erreur chargement tâches:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/login');
      } else {
        toast.error('Erreur lors du chargement des tâches');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchTodayTasks();
    // Rafraîchir toutes les 5 minutes
    const interval = setInterval(fetchTodayTasks, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/login');
  };

  const getIconForSection = (iconName) => {
    const icons = {
      'target': <Target size={20} />,
      'alert-triangle': <AlertTriangle size={20} />,
      'calendar': <Calendar size={20} />,
      'file-text': <FileText size={20} />,
      'zap': <Zap size={20} />,
      'flame': <Flame size={20} />
    };
    return icons[iconName] || <Target size={20} />;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'critical': { bg: '#FEE2E2', border: '#EF4444', text: '#DC2626' },
      'high': { bg: '#FEF3C7', border: '#F59E0B', text: '#D97706' },
      'medium': { bg: '#DBEAFE', border: '#3B82F6', text: '#2563EB' },
      'info': { bg: '#F3F4F6', border: '#9CA3AF', text: '#6B7280' }
    };
    return colors[priority] || colors.info;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-CA', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);
  };

  // Mémoiser les statistiques
  const stats = useMemo(() => {
    if (!data?.summary) return null;
    return data.summary;
  }, [data]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5' }}>
      {/* Header */}
      <header className="shadow-sm" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
                style={{ backgroundColor: '#F5F5F5' }}
              >
                <Home size={18} />
                Dashboard
              </button>
              <h1 className="text-2xl font-bold" style={{ color: '#1A1A1A' }}>
                📋 À faire aujourd'hui
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {lastRefresh && (
                <span className="text-sm" style={{ color: '#666666' }}>
                  Mis à jour: {lastRefresh.toLocaleTimeString('fr-CA')}
                </span>
              )}
              <button
                onClick={fetchTodayTasks}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                style={{ backgroundColor: '#1976D2', color: 'white' }}
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                Rafraîchir
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
                style={{ backgroundColor: '#F5F5F5' }}
              >
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading && !data ? (
          <div className="flex justify-center py-20">
            <RefreshCw size={32} className="animate-spin" style={{ color: '#1976D2' }} />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-sm" style={{ color: '#666666' }}>
                    <Target size={16} />
                    Total tâches
                  </div>
                  <div className="text-3xl font-bold" style={{ color: '#1976D2' }}>
                    {stats.total_items}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border-l-4" style={{ borderColor: '#EF4444' }}>
                  <div className="flex items-center gap-2 text-sm" style={{ color: '#666666' }}>
                    <AlertTriangle size={16} />
                    Critiques
                  </div>
                  <div className="text-3xl font-bold" style={{ color: '#EF4444' }}>
                    {stats.critical}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm border-l-4" style={{ borderColor: '#F59E0B' }}>
                  <div className="flex items-center gap-2 text-sm" style={{ color: '#666666' }}>
                    <Clock size={16} />
                    Priorité haute
                  </div>
                  <div className="text-3xl font-bold" style={{ color: '#F59E0B' }}>
                    {stats.high}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-sm" style={{ color: '#666666' }}>
                    <CheckCircle size={16} />
                    Sections
                  </div>
                  <div className="text-3xl font-bold" style={{ color: '#2E7D32' }}>
                    {stats.sections_count}
                  </div>
                </div>
              </div>
            )}

            {/* Sections */}
            {data?.sections?.length === 0 ? (
              <div className="bg-white rounded-lg p-12 text-center shadow-sm">
                <CheckCircle size={64} className="mx-auto mb-4" style={{ color: '#2E7D32' }} />
                <h2 className="text-xl font-bold mb-2">Tout est à jour ! 🎉</h2>
                <p style={{ color: '#666666' }}>Aucune tâche urgente pour aujourd'hui.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {data?.sections?.map((section) => {
                  const colors = getPriorityColor(section.priority);
                  return (
                    <div 
                      key={section.id} 
                      className="bg-white rounded-lg shadow-sm overflow-hidden"
                      style={{ borderLeft: `4px solid ${colors.border}` }}
                    >
                      {/* Section Header */}
                      <div 
                        className="px-4 py-3 flex items-center justify-between"
                        style={{ backgroundColor: colors.bg }}
                      >
                        <div className="flex items-center gap-3">
                          <span style={{ color: colors.text }}>
                            {getIconForSection(section.icon)}
                          </span>
                          <h2 className="font-semibold" style={{ color: colors.text }}>
                            {section.title}
                          </h2>
                          <span 
                            className="px-2 py-1 rounded-full text-sm font-medium"
                            style={{ backgroundColor: colors.border, color: 'white' }}
                          >
                            {section.count}
                          </span>
                        </div>
                      </div>

                      {/* Section Items */}
                      <div className="divide-y">
                        {section.items.map((item, index) => (
                          <div 
                            key={item.id || index}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => {
                              if (section.id.includes('deal') || section.id === 'hot_deals_no_action') {
                                navigate('/admin/pipeline');
                              } else if (section.id === 'submissions_pending') {
                                navigate('/admin/pipeline');
                              } else if (section.id === 'appointments_today') {
                                navigate('/admin');
                              }
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium" style={{ color: '#1A1A1A' }}>
                                    {item.title || item.deal_title || item.customer_name || `#${item.id?.slice(0, 8)}`}
                                  </span>
                                  {item.score && (
                                    <span 
                                      className="px-2 py-0.5 rounded text-xs font-medium"
                                      style={{ 
                                        backgroundColor: item.score >= 50 ? '#FEF3C7' : '#F3F4F6',
                                        color: item.score >= 50 ? '#D97706' : '#6B7280'
                                      }}
                                    >
                                      Score: {item.score}
                                    </span>
                                  )}
                                  {item.stage && (
                                    <span 
                                      className="px-2 py-0.5 rounded text-xs"
                                      style={{ backgroundColor: '#E5E7EB', color: '#374151' }}
                                    >
                                      {item.stage}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-sm" style={{ color: '#666666' }}>
                                  {item.contact_name && (
                                    <span className="flex items-center gap-1">
                                      <User size={14} />
                                      {item.contact_name}
                                    </span>
                                  )}
                                  {item.next_action_description && (
                                    <span className="flex items-center gap-1">
                                      <Target size={14} />
                                      {item.next_action_description}
                                    </span>
                                  )}
                                  {item.next_action_at && (
                                    <span className="flex items-center gap-1">
                                      <Clock size={14} />
                                      {formatDate(item.next_action_at)}
                                    </span>
                                  )}
                                  {item.days_since_sent !== undefined && (
                                    <span className="flex items-center gap-1">
                                      <Clock size={14} />
                                      {item.days_since_sent}j depuis envoi
                                    </span>
                                  )}
                                  {item.total_price && (
                                    <span className="flex items-center gap-1">
                                      <DollarSign size={14} />
                                      {formatCurrency(item.total_price)}
                                    </span>
                                  )}
                                  {item.estimated_value && (
                                    <span className="flex items-center gap-1">
                                      <DollarSign size={14} />
                                      {formatCurrency(item.estimated_value)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <ChevronRight size={20} style={{ color: '#9CA3AF' }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
